<template>

<div>

	<v-form v-show="!fillOrderStatus.formSubmitted"
		ref="form" v-model="valid" lazy-validation
	>
		<v-row>
			<v-col>
				<v-text-field
					outlined dense color="gray" persistent-hint
					:label="$t('common.orderForm.label.amountToFill')"
					v-model.number="input.fillAmount"
					:rules="rules.fillAmount"
					type="number"
					placeholder=""
					:suffix="parsedOrder.metaData.takerAsset.symbol"
					:hint="fillableAmounts + ', ' +
						$t('common.noun.allowance') + ': ' +
						formatAllowanceNumber(myaccount.allowanceByAddress[parsedOrder.takerAssetAddress])"
					>
				</v-text-field>
			</v-col>
		</v-row>

		<v-row>
			<v-col class="text-center">
				<v-btn color="success" :disabled="!valid || fillOrderStatus.waitingForNetwork"
					@click="fillOrder"
				>
					{{ $t('compOrderSummary.btnFillOrder') }} <ButtonLoading v-show="fillOrderStatus.waitingForNetwork" class="ml-3" />
				</v-btn>
			</v-col>
		</v-row>
	</v-form>
	<!-- END fill order form -->

	<!-- BEGIN Fill Order Results -->
	<div v-if="fillOrderStatus.waitingForNetwork && !fillOrderStatus.txHash" class="my-2 pa-3 bg-white">
		<ButtonLoading class="mr-3" /> {{ $t('compOrderSummary.statusWaitingMatamask') }}
	</div>

	<div class="text-center my-2 mx-3" v-if="fillOrderStatus.txHash && !fillOrderStatus.result">
		<PendingTransaction :txhash="fillOrderStatus.txHash" :txurl="networkConfig.txURL"></PendingTransaction>
	</div>

	<div v-if="fillOrderStatus.result && fillOrderStatus.result == 'success'" class="text-center mb-3">
		<div class="mb-3">{{ $t('compOrderSummary.statusSuccess') }}</div>
		<div v-if="ismodal">
			<v-btn color="success" @click="emitCloseModal">{{ $t('common.verb.close') }}</v-btn>
		</div>
		<div v-else>
			<v-btn color="success" :to="{ name: 'TradeHistory' }">{{ $t('compOrderSummary.btnViewTradeHistory') }}</v-btn>
		</div>
	</div>

	<div v-if="fillOrderStatus.networkError">
		<div class="error-message" style="max-height: 200px; overflow-y: scroll;">{{ $t('compOrderSummary.statusFailed') }}: {{ fillOrderStatus.networkError }}</div>
		<div class="my-3 text-center">
			<v-btn color="success" @click="retryFillOrder">{{ $t('common.verb.tryAgain')}}</v-btn>
		</div>
	</div>
	<!-- END Fill Order Results -->

</div>

</template>

<script>
import { config } from '@/config'
import { BigNumber } from '@0x/utils'


// import { BigNumber, orderHashUtils } from '0x.js'
import { Web3Wrapper } from '@0x/web3-wrapper'
import {mapActions, mapGetters, mapState} from 'vuex'
import Preloader from "@/components/Preloader"
import PendingTransaction from "@/components/PendingTransaction"
import AmountNumber from "@/components/AmountNumber"
import { formatAmountNumber, formatAllowanceNumber, countDecimals, calculateProtocolFee, getCurrentGasPrice } from '@/utils/utils'
import ButtonLoading from "@/components/ButtonLoading"
import { TX_DEFAULTS } from '@/config/constants'

export default {
	name: 'FillOrderForm',
	components: {
		Preloader,
		PendingTransaction,
		AmountNumber,
		ButtonLoading
	},
	props: {
		parsedOrder: { type: Object, required: true },
		orderState: { type: Object, required: true },
		myaccount: { type: Object, required: true },
		myaddress: { type: String, required: true },
		gasPrice: { type: Number, required: true },
		ismodal: { type: Boolean, default: false },
	},
	data () {
		return {
			config: config,
			show: false,
			valid: true,
			input: {
				fillAmount: null
			},
			fillOrderStatus: {
				formSubmitted: false,
				txHash: null,
				txReceipt: null,
				networkError: null,
				waitingForNetwork: false,
				result: null,
			},
			rules: {
				fillAmount: [
					// Field is required
					val => (val !== null && val !== undefined && val !== '') || this.$t('common.phrase.fieldIsRequired') + ' (' + this.fillableAmounts + ')',
					// Must be a number
					val => !isNaN(val) || this.$t('common.err.amount.mustNumber') + ' (' + this.fillableAmounts + ')',
					// Must greater than zero
					val => val > 0 || this.$t('common.err.amount.mustGreaterThanZero') + ' (' + this.fillableAmounts + ')',
					// Must not greater than the current balance
					val => val <= this.myaccount.balanceByAddress[this.parsedOrder.takerAssetAddress] || this.$t('common.err.amount.insufficientBalance') + ' (' + this.fillableAmounts + ')',
						// ": (" + this.$t('common.err.amount.max') + ': ' +
						// this.myaccount.balanceByAddress[this.parsedOrder.takerAssetAddress] + ')',
					// Must not greater than the current allowance
					val => val <= this.myaccount.allowanceByAddress[this.parsedOrder.takerAssetAddress] || this.$t('common.err.amount.insufficientAllowance')  + ' (' + this.fillableAmounts + ')',
						// + ": (" + this.$t('common.err.amount.max') + ': ' +
						// this.myaccount.allowanceByAddress[this.parsedOrder.takerAssetAddress] + ')',
					// Must not greater than fillable amount
					val => val <= this.orderState.availableTakerAmount || this.$t('common.err.amount.maxValCanFill')  + ' (' + this.fillableAmounts + ')',
						// + formatAmountNumber(this.orderState.availableTakerAmount),
					// Must not have too many decimals
					val => (countDecimals(val) <= config.inputAmountMaxDecimals) ||
						this.$t('common.err.amount.tooManyDecimals')  + ' (' + this.fillableAmounts + ')',
						// + ": (" + this.$t('common.err.amount.max') + ': ' + config.inputAmountMaxDecimals + ')',
				],
			}
		}
	},
	computed: {
		...mapGetters({
			network: 'getNetwork',
			account: 'getAccount',
			tokenInfo: 'getTokenInfo',
			myAddress: 'getUserAccount',
			web3Wrapper: 'getWeb3Wrapper',
			providerEngine: 'getProviderEngine',
			contractWrappers: 'getContractWrappers',
			contractAddresses: 'getContractAddresses',
			networkConfig: 'getNetworkConfig',
		}),
		maxFillableAmount: function () {
			if (this.orderState.selfFillable) {
				const balance = this.myaccount.balanceByAddress[this.parsedOrder.takerAssetAddress]
				const allowance = this.myaccount.allowanceByAddress[this.parsedOrder.takerAssetAddress]
				const availableTakerAmount = this.orderState.availableTakerAmount
				return Math.min(balance, allowance, availableTakerAmount)
			}
			else {
				return ''
			}
		},
		fillableAmounts: function () {
			const display = this.$t('common.noun.max') + ': ' +
				formatAmountNumber(this.maxFillableAmount) + ', ' +
				this.$t('common.noun.amountFillable') + ': ' +
				formatAmountNumber(this.orderState.availableTakerAmount) + ', ' +
				this.$t('common.noun.balance') + ': ' +
				formatAmountNumber(this.myaccount.balanceByAddress[this.parsedOrder.takerAssetAddress])
			return display
		}
	},
	methods: {
		...mapActions(['loadNetwork', 'loadTokenInfo', 'loadAccount']),

		/* ------------------------------------------------------ */
		// async init () {
		//   // console.log('UnwrapETH init')
		//   // console.log(this.parsedOrder)
		//   this.initializationDone = false
		//   if (this.networkNeedReload) {
		//     await this.loadNetwork()
		//   }
		//   if (this.tokenInfoNeedReload) {
		//     await this.loadTokenInfo()
		//   }
		//   if (this.accountNeedReload) {
		//     await this.loadAccount()
		//   }
		//   this.initializationDone = true
		// },
		formatAmountNumber: formatAmountNumber,
		formatAllowanceNumber: formatAllowanceNumber,
		showModal () {
			this.show = true
		},
		hideModal () {
			this.show = false
		},

		async retryFillOrder () {
			// this.initialOrderStateLoadingDone = false
			// await this.checkOrderState()
			this.$emit('check-order-state-request')
			this.input.fillAmount = null
			this.fillOrderStatus.formSubmitted = false
			this.fillOrderStatus.txHash = null
			this.fillOrderStatus.txReceipt = null
			this.fillOrderStatus.networkError = null
			this.fillOrderStatus.waitingForNetwork = false
			this.fillOrderStatus.result = null
		},

		async fillOrder () {

			// console.log('fill order')
			this.fillOrderStatus.formSubmitted = true
			this.fillOrderStatus.waitingForNetwork = true
			this.fillOrderStatus.networkError = null
			this.fillOrderStatus.result = null
			if (!this.parsedOrder.signedOrder) {
				throw new Error("Invalid input")
			}

			// console.log(this.parsedOrder)

			const fillAmount = this.input.fillAmount
			const decimals = this.parsedOrder.metaData.takerAsset.decimals
			let fillTakerAssetAmount = null
			try {
				fillTakerAssetAmount = new BigNumber(Web3Wrapper.toBaseUnitAmount(new BigNumber(fillAmount), decimals))
			} catch (err) {
				console.log(err)
				this.fillOrderStatus.networkError = "Error: " + err
				this.fillOrderStatus.waitingForNetwork = false
				this.fillOrderStatus.result = 'failure'
			}

			// console.log('filledTakerTokenAmount', fillTakerAssetAmount)

			const signedOrder = this.parsedOrder.signedOrder
			// console.log(signedOrder)
			this.fillOrderStatus.waitingForNetwork = true

			try {
				// const gasPrice = await getCurrentGasPrice(this.network.id, true)
				const gasPrice = this.gasPrice
				this.fillOrderStatus.txHash = await this.contractWrappers.exchange
        .fillOrder(signedOrder, fillTakerAssetAmount, signedOrder.signature)
        .sendTransactionAsync({
            from: this.myaddress,
            // gas: TX_DEFAULTS.gas,
            gasPrice: gasPrice,
            value: calculateProtocolFee([signedOrder], gasPrice),
        })
			} catch (err) {
				console.log(err)
				if (err.toString().includes('denied transaction signature')) {
					this.fillOrderStatus.networkError = 'MetaMask rejected transaction.'
				}
				else {
					this.fillOrderStatus.networkError = "Error filling order when submitting request: " + JSON.stringify(err)
				}
				this.fillOrderStatus.waitingForNetwork = false
				this.fillOrderStatus.result = 'failure'
				return
			}

			// Save data to backend
			const apiURL = `${this.config.backendAPIURL}/${this.network.id}/order-fill`
			const apiData = {
				networkId: this.network.id,
				orderHash: this.parsedOrder.orderHash,
				transactionHash: this.fillOrderStatus.txHash,
				signedOrder: signedOrder,
				metaData: this.parsedOrder.metaData,
				filledTakerAssetAmount: fillTakerAssetAmount,
			}
			// console.log('apiData', apiData)
			try {
				this.$http.post(apiURL, apiData)  // no need to await
			} catch (err) {
				console.log(err)
			}

			try {
				const txReceipt = await this.web3Wrapper.awaitTransactionMinedAsync(this.fillOrderStatus.txHash)
				console.log('FillOrder transaction receipt: ', txReceipt);
				this.fillOrderStatus.txReceipt = txReceipt
				await this.loadAccount(true)
				this.fillOrderStatus.result = 'success'
				this.$emit('check-order-state-request')
			} catch (err) {
				console.log(err)
				this.fillOrderStatus.networkError = "Error filling order when waiting for transaction being mined: " + err
				this.fillOrderStatus.result = 'failure'
			} finally {
				this.fillOrderStatus.waitingForNetwork = false
			}

		},
		emitLoading (orderHash) {
			this.$emit('cancel-order-loading', orderHash)
		},
		emitFailure (orderHash) {
			this.$emit('cancel-order-failure', orderHash)
		},
		emitComplete (orderHash) {
			this.$emit('cancel-order-complete', orderHash)
		},
		emitCloseModal () {
      this.$emit('fill-order-form-close-modal')
    },
	},
	created () {
		// this.init()
	},
	beforeDestroy () {
	},
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
